import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import CheckFlatIcon from 'components/ReusableComponents/Icons/CheckFlat'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import PaperplaneIcon from 'components/ReusableComponents/Icons/PaperPlane'
import WaitIcon from 'components/ReusableComponents/Icons/Wait'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import ImageGallery from 'components/ReusableComponents/Messaging/ConversationBubble/ImageGallery'
import AnnouncementResults from 'features/Announcements/AnnouncementResults'
import { customCampaignStatusTypes as statusTypes } from 'features/CustomCampaigns/helpers'
import usePlausible from 'hooks/usePlausible'
import moment from 'moment-timezone'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import campaignApi from 'services/api/campaign'
import config from 'services/config'

import styles from './ConversationBubble.module.scss'
import Initials from './Initials'

const ConversationBubble = ({
  messageTemplate,
  deliveryRemaining,
  deliverySize,
  deliveryFinishDate,
  messageMediaUrls,
  addedBy,
  deliveryStartDate,
  addedOn,
  customDeliveryStatus,
  contentId,
  isNewMessage,
  audience
}) => {
  const hotelId = useSelector(selectCurrentHotelId)
  const hotelName = useSelector(selectCurrentHotelName)

  const [focusedImage, setFocusedImage] = useState()

  const getImgUrl = fileName => `${config.inviteMediaBaseUrl}/${hotelId}/${fileName}`

  const timeZoneId = useSelector(s => s.hotelDetails.ianaTimeZoneId)

  const [statusOverwrite, setStatusOverwrite] = useState(null)

  const [isResultsVisible, setIsResultsVisible] = useState(false)

  const { postEvent, plausibleEvents } = usePlausible()

  if (customDeliveryStatus === statusTypes.Deleted) {
    return null
  }

  function handleClickShowResults() {
    postEvent(plausibleEvents.ViewAnnouncementResults, {
      contentId
    })

    setIsResultsVisible(!isResultsVisible)
  }

  function handleCancel() {
    campaignApi
      .updateCustomCampaign({
        contentId,
        customDeliveryStatus:
          customDeliveryStatus === statusTypes.Approved
            ? statusTypes.Deleted
            : statusTypes.Interrupted
      })
      .then(data => {
        setStatusOverwrite(data.customDeliveryStatus)
      })
  }

  const status = statusOverwrite || customDeliveryStatus

  const approxDeliveryEndDate = moment(deliveryStartDate).add(
    Math.floor(deliverySize / 30) * 5 + 5,
    'minutes'
  )

  const deliveryTimeDiff = moment(
    moment().tz(timeZoneId).format('YYYY/MM/DD hh:mm A')
  ).diff(moment(approxDeliveryEndDate.format('YYYY/MM/DD hh:mm A')), 'minute')

  return (
    <div
      className={[
        styles.containerSecondary,
        messageMediaUrls.length > 0 ? styles.containerWithMedia : '',
        isNewMessage ? styles.newMessage : ''
      ].join(' ')}
    >
      {isResultsVisible && (
        <AnnouncementResults
          contentId={contentId}
          onClose={() => setIsResultsVisible(false)}
        />
      )}
      <div className={styles.initialsHolder}>
        <Initials senderName={addedBy} />
      </div>
      <div className={styles.contentHolder}>
        <div>
          {messageMediaUrls.length > 0 && (
            <div className={styles.mediaHolder}>
              {messageMediaUrls.map(m => (
                <FilePreview
                  key={m}
                  src={getImgUrl(m)}
                  onClick={() => setFocusedImage(getImgUrl(m))}
                  showFileName
                  extension={getImgUrl(m)
                    .match(/\.([a-z]+)$/)?.[1]
                    ?.toLowerCase()}
                />
              ))}
            </div>
          )}
          <div className={styles.starterMain}>
            <div className={styles.starterBubble}>
              <div className={styles.name}>{addedBy} - </div>
              <div className={styles.time}>
                Added on {moment(addedOn).format('h:mm A')}
              </div>
              <div className={styles.text}>
                {messageTemplate &&
                  messageTemplate
                    .replace('@[hotelName](__hotelName__)', hotelName)
                    .replace(
                      '@[guestName](__guestName__)',
                      '⌘⌘@[guestName](__guestName__)⌘⌘'
                    )
                    .split('⌘⌘')
                    .map(p => {
                      return p === '@[guestName](__guestName__)' ? (
                        <span className={styles.variable}>@guestName</span>
                      ) : (
                        p
                      )
                    })}
              </div>
              <div className={styles.progress}>
                <span>
                  {status === statusTypes.Completed && (
                    <>
                      <div className={styles.icon}>
                        <CheckFlatIcon fill="#66BB6A" width="16px" height="16px" />
                      </div>

                      <div>
                        <div className={styles.statusTitle} style={{ color: '#4079F5' }}>
                          Completed on on {moment(deliveryFinishDate).format('h:mm A')}
                        </div>
                        <div className={styles.statusDesc}>
                          {deliverySize - deliveryRemaining}/{deliverySize} sent
                        </div>
                      </div>
                    </>
                  )}
                  {status === statusTypes.Started && (
                    <>
                      <div className={styles.icon}>
                        <PaperplaneIcon width="23px" height="23px" fill="#4079F5" />
                      </div>

                      <div>
                        <div className={styles.statusTitle} style={{ color: '#4079F5' }}>
                          Sending {deliverySize - deliveryRemaining}/{deliverySize}{' '}
                          messages
                        </div>
                        <div className={styles.statusDesc}>
                          approx. {deliveryTimeDiff} min remaining{' '}
                          <div className={styles.tooltipHolder}>
                            <InfoIcon fill="#9A8A8A" width="14px" height="14px" />
                            <div className={styles.tooltip}>
                              <p className={styles.tooltipTitle}>How does it work?</p>
                              <p className={styles.tooltipContent}>
                                We send 30 messages at a time with 5 minute intervals.
                                Sending the initial message may take up to 5 minutes.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {status === statusTypes.Approved && (
                    <>
                      <div className={styles.icon}>
                        <WaitIcon width="18px" height="18px" />
                      </div>

                      <div>
                        <div className={styles.statusTitle} style={{ color: '#4079F5' }}>
                          Scheduled for{' '}
                          {moment(deliveryStartDate).format('MMM DD, h:mm A')}
                        </div>
                        <div className={styles.statusDesc}>
                          {deliverySize - deliveryRemaining}/{deliverySize} sent
                        </div>
                      </div>
                    </>
                  )}
                  {status === statusTypes.Interrupted && (
                    <>
                      <div className={styles.icon}>
                        <CrossIcon width="16px" height="16px" />
                      </div>
                      <div>
                        <div className={styles.statusTitle}>
                          Cancelled on {moment(deliveryFinishDate).format('h:mm A')}
                        </div>
                        <div className={styles.statusDesc}>
                          {deliverySize - deliveryRemaining}/{deliverySize} sent
                        </div>
                      </div>
                    </>
                  )}
                  {status === statusTypes.Deleted && (
                    <>
                      <div className={styles.icon}>
                        <CrossIcon width="16px" height="16px" />
                      </div>
                      <div>
                        <div className={styles.statusTitle}>Deleted</div>
                      </div>
                    </>
                  )}
                </span>
                {(status === statusTypes.Approved || status === statusTypes.Started) && (
                  <button className={styles.cancelButton} onClick={handleCancel}>
                    Cancel
                  </button>
                )}
                {status === statusTypes.Completed && audience === 'DayBeforeCheckOut' && (
                  <button
                    onClick={() => handleClickShowResults()}
                    className={styles.cancelButton}
                  >
                    Show results
                  </button>
                )}
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarInner}
                  style={{
                    width: `${((deliverySize - deliveryRemaining) * 100) / deliverySize}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {focusedImage && (
            <ImageGallery
              hideGallery={() => setFocusedImage(null)}
              imgUrl={focusedImage}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ConversationBubble
